import { Sheet, SheetContent, SheetTitle, Text } from "@ferrivia/ui";
import { useAppContext } from "../../app/useAppContext";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import { useAnalyteCopy, useRangeCopy } from "@ferrivia/lab-ui";
import s from "./AnalyteInfoSheet.module.css";

/** The reading that was tapped — identity and the lab's own metadata, never
    the measured value. The sheet explains what the measurement is; it has no
    business knowing what this client's came out at. */
export interface AnalyteInfo {
  analyteCode: string;
  unit: string;
  refText: string | null;
  labCode: string | null;
}

export interface AnalyteInfoSheetProps {
  analyte: AnalyteInfo | null;
  onClose: () => void;
  /** Portal target; defaults to the app root, which sits beneath full-screen overlays. */
  container?: HTMLElement | null;
  dismissKey?: string;
}

/* Tap a reading, get a plain-language explainer for that measurement.

   Every word here is static per analyte and identical for every client:
   what the measurement is, not what this client's came out at. The sheet is
   deliberately given the analyte's code, unit and reported range and *not*
   the value, so no future edit can quietly start varying the explanation
   with the measurement — that would be the interpretive step REQ-MDR-002
   forbids (client CLAUDE.md, "The software does not interpret"). The range
   shown is the same display string the reading carries. */
export function AnalyteInfoSheet({ analyte, onClose, container, dismissKey = "analyteInfo" }: AnalyteInfoSheetProps) {
  const { rootElement } = useAppContext();
  const { partsFor } = useRangeCopy();
  const { labelFor, explainerFor } = useAnalyteCopy();

  const open = analyte !== null;
  useDismissOnBack(open, onClose, dismissKey);

  // Kept mounted through the close animation, so the copy can't blank out
  // mid-slide — same reason UploadSheet resets on the way in.
  const explainer = analyte === null ? null : explainerFor(analyte.analyteCode);
  const parts = analyte === null ? null : partsFor(analyte.refText, analyte.labCode);

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent container={container ?? rootElement}>
        <SheetTitle className={s.title}>{analyte === null ? "" : labelFor(analyte.analyteCode)}</SheetTitle>
        <div className={s.body}>
          {explainer !== null && (
            <Text as="p" size="sm" className={s.explainer}>
              {explainer}
            </Text>
          )}

          {parts !== null && (
            <div className={s.meta}>
              <div className={s.metaRow}>
                <Text as="span" size="sm" className={s.metaTerm}>
                  {parts.term}
                </Text>
                {parts.range !== null && (
                  <Text as="span" size="sm" weight="medium" className={s.metaValue}>
                    {parts.range} {analyte?.unit}
                  </Text>
                )}
              </div>
              {parts.source !== null && (
                <Text as="p" size="xs" className={s.metaSource}>
                  {parts.source}
                </Text>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
